import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { useAppDispatch } from "../app/hooks";
import { loginSuccess } from "../features/login/login-slice";
import { auth, logInWithEmailAndPassword, signInWithGoogle } from "../firebase";

const signInSchema = z.object({
	email: z.string().email("Enter a valid email address"),
	password: z.string().min(8, "Password must be at least 8 characters"),
});

type SignInFormValues = z.infer<typeof signInSchema>;

function SignIn() {
	const [user, loading, authError] = useAuthState(auth);
	const [formError, setFormError] = useState<string | null>(null);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
		watch,
	} = useForm<SignInFormValues>({
		resolver: zodResolver(signInSchema),
		mode: "onChange",
	});
	const emailValue = watch("email", "");
		const passwordValue = watch("password", "");

	useEffect(() => {
		if (user) {
			dispatch(
				loginSuccess({
					username: user.displayName || user.email || "",
					email: user.email || "",
					uid: user.uid,
				})
			);
			navigate("/dashboard");
		} else if (authError) {
			setFormError(authError.message);
		}
	}, [authError, dispatch, navigate, user]);

	const onSubmit = async (values: SignInFormValues) => {
		setFormError(null);
		try {
			await logInWithEmailAndPassword(values.email, values.password);
		} catch (error) {
			if (error instanceof Error) {
				setFormError(error.message);
			} else {
				setFormError("Unable to log in. Please try again.");
			}
		}
	};

	const handleGoogleLogin = async () => {
		setFormError(null);
		try {
			await signInWithGoogle();
		} catch (error) {
			if (error instanceof Error) {
				setFormError(error.message);
			} else {
				setFormError("Unable to authenticate with Google.");
			}
		}
	};

	return (
		<section className="page page--auth">
			<div className="auth-card">
				<header>
					<p className="eyebrow">Bienvenido de nuevo</p>
					<h1>Inicia sesión</h1>
					<p>Centraliza tus auditorías y comparte resultados con tu equipo.</p>
				</header>
				<form className="auth-form" onSubmit={handleSubmit(onSubmit)} noValidate>
					<label htmlFor="email">Correo electrónico</label>
					<input
						id="email"
						type="email"
						className={clsx("auth-input", {
							"has-value": Boolean(emailValue),
							"input-error": Boolean(errors.email),
						})}
						placeholder="correo@empresa.com"
						autoComplete="email"
						{...register("email")}
					/>
					{errors.email && (
						<span className="form-error" role="alert">
							{errors.email.message}
						</span>
					)}

					<label htmlFor="password">Contraseña</label>
					<input
						id="password"
						type="password"
						className={clsx("auth-input", {
							"has-value": Boolean(passwordValue),
							"input-error": Boolean(errors.password),
						})}
						placeholder="********"
						autoComplete="current-password"
						{...register("password")}
					/>
					{errors.password && (
						<span className="form-error" role="alert">
							{errors.password.message}
						</span>
					)}

					<button
						className="button button--primary button--full"
						type="submit"
						disabled={isSubmitting || loading}
					>
						Iniciar sesión
					</button>
					<button
						className="button button--ghost button--full"
						type="button"
						onClick={handleGoogleLogin}
						disabled={isSubmitting || loading}
					>
						Entrar con Google
					</button>

					{formError && (
						<p className="form-error" role="alert">
							{formError}
						</p>
					)}

					<div className="auth-form__links">
						<Link to="/reset">¿Olvidaste tu contraseña?</Link>
						<Link to="/register">Crear cuenta</Link>
					</div>
				</form>
			</div>
		</section>
	);
}
export default SignIn;
